import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Header, IconHighlight, Pagination, StatusMessage, useToast } from '@aimsos/ui';
import { useStickyScroll } from '@aimsos/hooks';

import {
  DeleteWidgetModal,
  WidgetDetailPanel,
  WidgetLibraryFilterBar,
  WidgetLibraryFilterSlideout,
  WidgetLibraryRow,
  WidgetLibrarySkeleton,
} from '../components';
import { WIDGET_LIBRARY_LAYOUT_CLASS } from '../constants';
import { useWidgetCatalog, useWidgetLibraryFilters, WIDGET_LIBRARY_PAGE_SIZE_OPTIONS } from '../hooks';
import { deleteWidget } from '../../../services/widgets.js';

import { Boxes, LayoutGrid, Plus, SearchX, TriangleAlert } from 'lucide-react';

/**
 * Widget Library index route (CT-5185 shell + CT-5186 search/filters/sort +
 * CT-5187 rows/row actions + CT-5188 detail panel).
 *
 * The page is the single owner of what the row and the panel both act on: one
 * selected widget, one delete dialog, one set of handlers. That is why the
 * panel is presentational.
 *
 * Deleting is a **row** action only — the detail panel is where a widget is
 * read before it is placed, and a destructive action did not belong beside
 * the two that build on it.
 */
const WidgetLibraryPage = () => {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const toast = useToast();

  const filtersState = useWidgetLibraryFilters();
  const { query, clearAll, openDraft } = filtersState;
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  const showToolbar = !isScrolled || filtersVisible;
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [widgetPendingDelete, setWidgetPendingDelete] = useState(null);

  const {
    widgets,
    total,
    governedCount,
    currentPage,
    itemsPerPage,
    totalPages,
    rangeLabel,
    isLoading,
    isEmptyCatalog,
    isNoResults,
    error,
    onPageChange,
    onItemsPerPageChange,
    refetch,
  } = useWidgetCatalog({ query });

  const handleCreate = useCallback(() => {
    navigate('new');
  }, [navigate]);

  const handleOpenAllFilters = useCallback(() => {
    // Seed the staged draft from what is committed, so the panel opens showing
    // the filters already in effect rather than a blank slate.
    openDraft();
    setIsAllFiltersOpen(true);
  }, [openDraft]);

  const handleCloseAllFilters = useCallback(() => {
    setIsAllFiltersOpen(false);
  }, []);

  // One stable callback per intent, so the memoised rows do not re-render when
  // an unrelated row changes. The row supplies its own widget / id.
  const handlePreview = useCallback((widget) => {
    setSelectedWidget(widget);
  }, []);

  const handleClosePreview = useCallback(() => {
    setSelectedWidget(null);
  }, []);

  const handleEdit = useCallback(
    (widgetId) => {
      navigate(`${widgetId}/edit`);
    },
    [navigate],
  );

  const handleRequestDelete = useCallback((widget) => {
    setWidgetPendingDelete(widget);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setWidgetPendingDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(
    async (widget) => {
      // deleteWidget() throws on a non-2xx envelope, which keeps the row in
      // place and surfaces the message in the dialog footer.
      await deleteWidget(widget.id);
      setWidgetPendingDelete(null);
      if (selectedWidget?.id === widget.id) setSelectedWidget(null);
      refetch();
      toast.success({
        title: t('widgetLibrary.delete.toastTitle'),
        message: t('widgetLibrary.delete.toastMessage', { name: widget.name }),
      });
    },
    [refetch, selectedWidget?.id, t, toast],
  );

  const handleAddToDashboard = useCallback(() => {
    // TODO(CT-5183): reuse the existing dashboard-selection flow from
    // domains/dashboards once the widget→dashboard placement contract lands.
    // Enabled in V1 here and in the detail panel; only the post-save dialog
    // drops it (epic decision 6).
    toast.info({
      title: t('widgetLibrary.row.addToDashboardPendingTitle'),
      message: t('widgetLibrary.row.addToDashboardPendingMessage'),
    });
  }, [t, toast]);

  const handlePrevPage = useCallback(() => {
    onPageChange(Math.max(1, currentPage - 1));
  }, [currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    onPageChange(Math.min(totalPages, currentPage + 1));
  }, [currentPage, onPageChange, totalPages]);

  const skeletonCount = useMemo(
    () => Math.max(1, Math.min(itemsPerPage, 6)),
    [itemsPerPage],
  );

  const subtitle = useMemo(
    () => t('widgetLibrary.subtitle', { count: total, governed: governedCount }),
    [governedCount, t, total],
  );

  const headerTag = useMemo(() => ({ label: subtitle, variant: 'neutral' }), [subtitle]);

  // `{ label, onClick, leftIcon: Plus, size: 'md' }` is the list-page CTA shape
  // in both other studios — `HtlPacksListPage`, `TeamsListPage`,
  // `DestinationsPage`, `TruthPlaneListPageHeader`, `DriveListPageHeader`.
  // `size` matters: a header at `M` hands its CTA `sm`, so without the override
  // the button is a size smaller than everyone else's — and it would shrink
  // again as the header compresses.
  const headerPrimaryCta = useMemo(
    () => ({
      label: t('widgetLibrary.createWidget'),
      onClick: handleCreate,
      leftIcon: Plus,
      size: 'md',
    }),
    [handleCreate, t],
  );

  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('widgetLibrary.pagination.rowsPerPage'),
      items: t('widgetLibrary.pagination.items', { range: rangeLabel, total }),
      previousPage: t('widgetLibrary.pagination.previousPage'),
      nextPage: t('widgetLibrary.pagination.nextPage'),
    }),
    [rangeLabel, t, total],
  );

  const createAction = useMemo(
    () => (
      <Button variant="primary" leftIcon={Plus} onClick={handleCreate}>
        {t('widgetLibrary.createWidget')}
      </Button>
    ),
    [handleCreate, t],
  );

  const getResultsContent = () => {
    if (isLoading) {
      return (
        <div className={WIDGET_LIBRARY_LAYOUT_CLASS}>
          <WidgetLibrarySkeleton count={skeletonCount} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={TriangleAlert}
            title={t('widgetLibrary.error.title')}
            subtitle={t('widgetLibrary.error.subtitle')}
          />
          <Button variant="secondary" onClick={refetch}>
            {t('widgetLibrary.error.retry')}
          </Button>
        </div>
      );
    }

    if (isEmptyCatalog) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={LayoutGrid}
            title={t('widgetLibrary.empty.title')}
            subtitle={t('widgetLibrary.empty.subtitle')}
          />
          {createAction}
        </div>
      );
    }

    if (isNoResults) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={SearchX}
            title={t('widgetLibrary.noResults.title')}
            subtitle={t('widgetLibrary.noResults.subtitle')}
          />
          <Button variant="secondary" onClick={clearAll}>
            {t('widgetLibrary.noResults.clearFilters')}
          </Button>
        </div>
      );
    }

    return (
      <div className={WIDGET_LIBRARY_LAYOUT_CLASS}>
        {widgets.map((widget) => (
          <WidgetLibraryRow
            key={widget.id}
            widget={widget}
            selected={selectedWidget?.id === widget.id}
            onPreview={handlePreview}
            onEdit={handleEdit}
            onDelete={handleRequestDelete}
            onAddToDashboard={handleAddToDashboard}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto scrollbar-aimsos-auto">
        <div className="flex min-h-full flex-col">
          {/* Agentic Studio's networks list, mechanism for mechanism: pinned,
              compressing once the page moves, the backdrop only then — a
              permanent one reads as a toolbar rather than as a header that
              happens to stay put — and the filters folding away with it,
              returning on a held hover over the bar.

              The hover handlers are only wired while compressed: at rest the
              toolbar is already there, and arming them would make every pointer
              that crosses the header schedule work for nothing. */}
          <div
            className="sticky top-0 z-20 px-6"
            onMouseEnter={isScrolled ? onMouseEnter : undefined}
            onMouseLeave={isScrolled ? onMouseLeave : undefined}
          >
            {/* The vertical padding lives on the painted element, never on the
                sticky wrapper. Outside it, the backdrop stops at the header's
                own box and rows scroll visibly through the gutter above and
                below it. */}
            <div
              className={[
                'pb-6 pt-6 transition-colors duration-200',
                isScrolled ? 'bg-surface-base' : '',
              ].filter(Boolean).join(' ')}
            >
              {/* `Boxes`, not `LayoutGrid`: it is what the shell's sidebar shows
                  for Widgets, and the header should lead with the icon you
                  clicked to get here. */}
              <Header
                size={isScrolled ? 'compress' : 'M'}
                showIcon
                icon={<IconHighlight icon={Boxes} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
                title={t('widgetLibrary.title')}
                tag={headerTag}
                showDescription={!isScrolled}
                description={t('widgetLibrary.description')}
                primaryCta={headerPrimaryCta}
              />

              {/* Collapsed by CSS rather than unmounted. Agentic Studio pairs a
                  `mounted` flag with an `opacity` one so the fade has something
                  to run from, but both have to be written from inside an effect,
                  which `react-hooks/set-state-in-effect` rejects here. Animating
                  `max-height` alongside the opacity gets the same fold-away with
                  no state at all — and `inert` keeps the hidden toolbar off the
                  tab order, which the opacity-only half of that pair did not.

                  `overflow` only switches to `hidden` while collapsed: kept on
                  permanently, it clips the Category/Profile quick-filter
                  dropdowns, which render as an absolutely-positioned popover
                  inside this same wrapper — the popover would open with
                  `aria-expanded`/opacity correct and still be invisible. */}
              <div
                inert={showToolbar ? undefined : ''}
                className={[
                  'transition-all duration-200',
                  showToolbar ? 'mt-4 max-h-40 overflow-visible opacity-100' : 'mt-0 max-h-0 overflow-hidden opacity-0',
                ].join(' ')}
              >
                <WidgetLibraryFilterBar
                  filtersState={filtersState}
                  onAllFiltersClick={handleOpenAllFilters}
                />
              </div>
            </div>

            {/* Rows have to leave somewhere. Without this they reappear at full
                opacity a pixel under a hard edge; the fade is what makes the bar
                read as floating over the list rather than clipping it. */}
            {isScrolled ? (
              <div
                aria-hidden
                className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
              />
            ) : null}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4 px-6">
            <div className="min-w-0">{getResultsContent()}</div>

            {(total > 0 || isLoading) && (
              <Pagination
                className="sticky bottom-0 z-10 mt-auto bg-surface-base"
                rangeLabel={rangeLabel}
                totalItems={total}
                currentPage={currentPage}
                totalPages={totalPages}
                hasPrevious={currentPage > 1}
                hasNext={currentPage < totalPages}
                perPage={itemsPerPage}
                pageSizeOptions={WIDGET_LIBRARY_PAGE_SIZE_OPTIONS}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                onPageSizeChange={onItemsPerPageChange}
                disabled={total === 0}
                ariaLabelPerPage={t('widgetLibrary.pagination.rowsPerPage')}
                labels={paginationLabels}
              />
            )}
          </div>
        </div>
      </div>

      <WidgetLibraryFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={handleCloseAllFilters}
        filtersState={filtersState}
      />

      {/* Keyed on the open widget so the panel's own state — the preview size —
          starts fresh on every open, including reopening the same widget. */}
      <WidgetDetailPanel
        key={selectedWidget?.id ?? 'closed'}
        open={Boolean(selectedWidget)}
        widget={selectedWidget}
        onClose={handleClosePreview}
        onEdit={handleEdit}
        onAddToDashboard={handleAddToDashboard}
      />

      <DeleteWidgetModal
        isOpen={Boolean(widgetPendingDelete)}
        widget={widgetPendingDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default WidgetLibraryPage;
